import React, { useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';

const SERVICE_TYPES_ENDPOINT = '/ServiceTypes';
const SERVICES_ENDPOINT = '/Services/GetMinimalServiceInfo';
const CUSTOM_ADDITIONAL_SERVICE_ENDPOINT =
  '/customAdditionalServiceManagement/list';
const CUSTOM_ADDITIONAL_SERVICE_MANAGEMENT_ENDPOINT =
  '/customAdditionalServiceManagement/Manage';

const CustomAdditionalServiceManagement = () => {
  const { get: getServiceTypes } = useFetch(SERVICE_TYPES_ENDPOINT);
  const { get: getServices } = useFetch(SERVICES_ENDPOINT);

  const { get: getCustomAdditionalServiceConfiguration } = useFetch(
    CUSTOM_ADDITIONAL_SERVICE_ENDPOINT,
  );

  const { post: manageCustomAdditionalService } = useFetch(
    CUSTOM_ADDITIONAL_SERVICE_MANAGEMENT_ENDPOINT,
  );

  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [existingConfigurations, setExistingConfigurations] = useState([]);

  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');

  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);

  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingConfigurations, setLoadingConfigurations] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!selectedServiceTypeId) {
      setExistingConfigurations([]);
      setSelectedServiceId('');
      setServiceSearch('');
      setServiceDropdownOpen(false);
      return;
    }

    setSelectedServiceId('');
    setServiceSearch('');
    setServiceDropdownOpen(false);
    loadExistingConfigurations(selectedServiceTypeId);
  }, [selectedServiceTypeId]);

  const loadInitialData = async () => {
    setLoadingServiceTypes(true);
    setLoadingServices(true);
    setError('');

    try {
      const [serviceTypesResponse, servicesResponse] = await Promise.all([
        getServiceTypes(),
        getServices(),
      ]);

      setServiceTypes(normalizeArrayResponse(serviceTypesResponse));
      setServices(normalizeArrayResponse(servicesResponse));
    } catch (err) {
      setError('Nepavyko įkelti paslaugų tipų arba paslaugų.');
    } finally {
      setLoadingServiceTypes(false);
      setLoadingServices(false);
    }
  };

  const loadExistingConfigurations = async (serviceTypeId) => {
    setLoadingConfigurations(true);
    setError('');

    try {
      const configurationResponse =
        await getCustomAdditionalServiceConfiguration(
          `?serviceTypeId=${serviceTypeId}`,
        );

      setExistingConfigurations(normalizeArrayResponse(configurationResponse));
    } catch (err) {
      setError('Nepavyko įkelti esamos konfigūracijos.');
    } finally {
      setLoadingConfigurations(false);
    }
  };

  const selectedServiceType = useMemo(() => {
    return serviceTypes.find(
      (x) => Number(x.serviceTypeId ?? x.id) === Number(selectedServiceTypeId),
    );
  }, [serviceTypes, selectedServiceTypeId]);

  const selectedService = useMemo(() => {
    return services.find(
      (x) => Number(x.serviceId ?? x.id) === Number(selectedServiceId),
    );
  }, [services, selectedServiceId]);

  const configuredServiceIds = useMemo(() => {
    return new Set(
      existingConfigurations.map((configuration) =>
        Number(configuration.serviceId),
      ),
    );
  }, [existingConfigurations]);

  const availableServices = useMemo(() => {
    return services.filter((service) => {
      const serviceId = Number(service.serviceId ?? service.id);

      return !configuredServiceIds.has(serviceId);
    });
  }, [services, configuredServiceIds]);

  const filteredServices = useMemo(() => {
    const search = serviceSearch.trim().toLowerCase();

    if (!search) {
      return availableServices;
    }

    return availableServices.filter((service) => {
      const title = getServiceTitle(service).toLowerCase();
      const amisServiceId = String(service.amisServiceId ?? '').toLowerCase();
      const externalId = String(service.externalId ?? '').toLowerCase();
      const serviceId = String(
        service.serviceId ?? service.id ?? '',
      ).toLowerCase();

      return (
        title.includes(search) ||
        amisServiceId.includes(search) ||
        externalId.includes(search) ||
        serviceId.includes(search)
      );
    });
  }, [availableServices, serviceSearch]);

  const existingConfigurationRows = useMemo(() => {
    return existingConfigurations.map((configuration) => {
      const service = services.find((x) => {
        const serviceId = Number(x.serviceId ?? x.id);

        return serviceId === Number(configuration.serviceId);
      });

      return {
        ...configuration,
        service,
      };
    });
  }, [existingConfigurations, services]);

  const canSave = Boolean(
    selectedServiceTypeId && selectedServiceId && !saving,
  );

  const handleServiceTypeChange = (event) => {
    setSelectedServiceTypeId(event.target.value);
  };

  const handleServiceSearchChange = (event) => {
    setServiceSearch(event.target.value);
    setSelectedServiceId('');
    setServiceDropdownOpen(true);
  };

  const handleServiceSelect = (service) => {
    const serviceId = service.serviceId ?? service.id;

    setSelectedServiceId(String(serviceId));
    setServiceSearch(getServiceTitle(service));
    setServiceDropdownOpen(false);
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      await manageCustomAdditionalService({
        serviceTypeId: Number(selectedServiceTypeId),
        serviceId: Number(selectedServiceId),
        remove: false,
      });

      setSelectedServiceId('');
      setServiceSearch('');
      setServiceDropdownOpen(false);

      await loadExistingConfigurations(selectedServiceTypeId);
    } catch (err) {
      setError('Nepavyko išsaugoti konfigūracijos.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (serviceId) => {
    if (!selectedServiceTypeId || !serviceId) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      await manageCustomAdditionalService({
        serviceTypeId: Number(selectedServiceTypeId),
        serviceId: Number(serviceId),
        remove: true,
      });

      await loadExistingConfigurations(selectedServiceTypeId);
    } catch (err) {
      setError('Nepavyko pašalinti konfigūracijos.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <Heading title="Papildomų paslaugų valdymas" />

      <Card>
        <CardHeader>
          <div className="space-y-1">
            <h2 className="text-xl font-semibold">
              Pridėti papildomos paslaugos konfigūraciją
            </h2>

            <p className="text-sm text-muted-foreground">
              Pasirinkite paslaugos tipą, tada pasirinkite paslaugą, kuri turi
              būti priskirta kaip papildoma paslauga.
            </p>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-5 mb-5">
            {error && (
              <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Paslaugos tipas</label>

                <select
                  className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={selectedServiceTypeId}
                  disabled={loadingServiceTypes || saving}
                  onChange={handleServiceTypeChange}
                >
                  <option value="">
                    {loadingServiceTypes
                      ? 'Įkeliami paslaugų tipai...'
                      : 'Pasirinkite paslaugos tipą'}
                  </option>

                  {serviceTypes.map((serviceType) => {
                    const id = serviceType.serviceTypeId ?? serviceType.id;

                    return (
                      <option key={id} value={id}>
                        {getServiceTypeTitle(serviceType)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Paslauga</label>

                <div className="relative">
                  <input
                    className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={serviceSearch}
                    disabled={
                      !selectedServiceTypeId || loadingServices || saving
                    }
                    placeholder={
                      !selectedServiceTypeId
                        ? 'Pirmiausia pasirinkite paslaugos tipą'
                        : loadingServices
                          ? 'Įkeliamos paslaugos...'
                          : 'Ieškokite pagal pavadinimą arba ID'
                    }
                    onChange={handleServiceSearchChange}
                    onFocus={() => {
                      if (selectedServiceTypeId && !loadingServices) {
                        setServiceDropdownOpen(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setServiceDropdownOpen(false);
                      }, 150);
                    }}
                  />

                  {serviceDropdownOpen &&
                    selectedServiceTypeId &&
                    !loadingServices && (
                      <div className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md border bg-background shadow-lg">
                        {filteredServices.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-muted-foreground">
                            Paslaugų nerasta.
                          </div>
                        ) : (
                          filteredServices.map((service) => {
                            const id = service.serviceId ?? service.id;

                            return (
                              <button
                                key={id}
                                type="button"
                                className="block w-full px-3 py-3 text-left text-sm hover:bg-muted"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleServiceSelect(service)}
                              >
                                <div className="font-medium">
                                  {getServiceTitle(service)}
                                </div>

                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span>Paslaugos ID: {id}</span>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {selectedServiceType && (
              <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
                Pasirinktas paslaugos tipas:{' '}
                <span className="font-medium">
                  {getServiceTypeTitle(selectedServiceType)}
                </span>
              </div>
            )}

            {selectedService && (
              <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
                Pasirinkta paslauga:{' '}
                <span className="font-medium">
                  {getServiceTitle(selectedService)}
                </span>
              </div>
            )}

            {canSave && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  disabled={!canSave}
                  onClick={handleSave}
                >
                  {saving ? 'Saugoma...' : 'Išsaugoti konfigūraciją'}
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedServiceTypeId && (
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">Esamos konfigūracijos</h2>

              <p className="text-sm text-muted-foreground">
                Paslaugos, kurios jau priskirtos pasirinktam paslaugos tipui.
              </p>
            </div>
          </CardHeader>

          <CardContent>
            {loadingConfigurations ? (
              <div className="text-sm text-muted-foreground">
                Įkeliamos esamos konfigūracijos...
              </div>
            ) : existingConfigurationRows.length === 0 ? (
              <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                Šiam paslaugos tipui papildomos paslaugos dar nepriskirtos.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border mb-5">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">
                        Paslaugos ID
                      </th>

                      <th className="px-4 py-3 text-left font-medium">
                        Paslaugos pavadinimas
                      </th>

                      <th className="px-4 py-3 text-right font-medium">
                        Veiksmai
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {existingConfigurationRows.map((row) => (
                      <tr
                        key={`${row.serviceTypeId}-${row.serviceId}`}
                        className="border-t"
                      >
                        <td className="px-4 py-3">{row.serviceId}</td>

                        <td className="px-4 py-3">
                          {row.service
                            ? getServiceTitle(row.service)
                            : 'Paslauga neįkelta'}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-60"
                            disabled={saving}
                            onClick={() => handleRemove(row.serviceId)}
                          >
                            Pašalinti
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const normalizeArrayResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.result)) {
    return response.result;
  }

  return [];
};

const getServiceTypeTitle = (serviceType) => {
  if (!serviceType) {
    return '';
  }

  return (
    serviceType.serviceTypeTitle ??
    serviceType.title ??
    serviceType.name ??
    `Paslaugos tipas ${serviceType.serviceTypeId ?? serviceType.id}`
  );
};

const getServiceTitle = (service) => {
  if (!service) {
    return '';
  }

  return (
    service.title ??
    service.name ??
    service.serviceTitle ??
    `Paslauga ${service.serviceId ?? service.id}`
  );
};

export default CustomAdditionalServiceManagement;
